import { Box, Collapse } from "@mui/material";
import { useState } from "react";
import { NavigationConfig } from "@/theme";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import IconLoader from "@/features/Common/components/IconLoader";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";

export interface DefentSheetProps {
  closeButton?: boolean;
  expanded?: boolean;
  children: React.ReactNode;
  padding?: number;
  onClose: () => void;
  onExpandedChange?: (expanded: boolean) => void;
}

export default function DefentSheet({
  closeButton = true,
  expanded = false,
  children,
  padding = 1,
  onClose,
  onExpandedChange,
}: DefentSheetProps) {
  const { t } = useTranslation();
  const [localExpanded, setLocalExpanded] = useState(expanded);
  const isExpanded = onExpandedChange ? expanded : localExpanded;

  useLockBodyScroll(isExpanded);

  const handleToggle = () => {
    if (onExpandedChange) {
      onExpandedChange(!isExpanded);
    } else {
      setLocalExpanded(!localExpanded);
    }

    // Call onClose when closing (going from expanded to collapsed)
    if (isExpanded && onClose) {
      onClose();
    }
  };

  return (
    <Box>
      {/* Overlay when expanded */}
      {isExpanded && (
        <Box
          data-testid="overlay"
          onClick={handleToggle}
          sx={{
            position: "fixed",
            inset: 0,
            width: "100%",
            minHeight: "100dvh",
            "@supports (height: -webkit-fill-available)": {
              minHeight: "-webkit-fill-available",
            },
            opacity: 0.6,
            bgcolor: "black",
            zIndex: 999,
            overscrollBehavior: "contain",
            touchAction: "none",
          }}
        />
      )}
      <Box sx={{ position: "relative", overflow: "scroll" }}>
        <Box
          sx={{
            position: "fixed",
            bottom: isExpanded ? 0 : -100,
            overflow: "scroll",
            overscrollBehavior: "contain",
            left: 0,
            right: 0,
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: 2,
            paddingRight: 2,
            margin: "auto",
            maxWidth: "600px",
            width: "100%",
            maxHeight: "100dvh",
            zIndex: 10001,
            bgcolor: "white",
            borderTopLeftRadius: 22,
            borderTopRightRadius: 22,
            boxShadow: isExpanded ? "" : NavigationConfig.boxShadow,
          }}
        >
          {closeButton && (
            <IconButton
              aria-label={t("common.modalCloseButton")}
              onClick={handleToggle}
              sx={{
                marginTop: 3,
                marginBottom: 3,
                marginLeft: 1,
                marginRight: 1,
                position: "absolute",
                right: 0,
                top: 0,
              }}
            >
              <IconLoader icon="CrossIcon" />
            </IconButton>
          )}

          <Collapse in={isExpanded}>
            <Box
              sx={{
                bgcolor: "white",
                pt: padding,
                pb: padding,
              }}
            >
              <Box sx={{ py: padding }}>{children}</Box>
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
}
